import moment from 'moment';
import { DEVICE_STATUS, FILTER_ALL, PERIOD_TYPE } from './heatPumpDevice';
import { HEATCAST_NUMBER_KEYS } from './heatcastResult';

export const sum = (list, fn) => list.reduce((s, x) => s + fn(x), 0);

export const fmt = (v, d = 1) =>
  Number(v).toLocaleString('ko-KR', { minimumFractionDigits: d, maximumFractionDigits: d });

// HEATCAST 결과용 포맷 (숫자가 아니면 '—')
export const fmtValue = (n, unit = '', digits = 1) =>
  typeof n === 'number' && Number.isFinite(n)
    ? n.toLocaleString('ko-KR', { maximumFractionDigits: digits }) + unit
    : '—';

const hourLabel = (i) => `${String(i).padStart(2, '0')}시`;

export const filterDevices = (devices, region, maker) =>
  devices.filter(
    (x) => (region === FILTER_ALL || x.region === region) && (maker === FILTER_ALL || x.maker === maker)
  );

export const getSummary = (devices) => {
  const running = devices.filter((x) => x.status === DEVICE_STATUS.RUN);
  const total = devices.length;
  const kw = sum(devices, (x) => x.kw);
  const today = sum(devices, (x) => x.today);
  const heat = sum(running, (x) => x.kw * x.cop);
  const cop = kw ? heat / kw : 0;
  const active = total ? (running.length / total) * 100 : 0;

  return {
    total,
    kw,
    today,
    cop,
    active,
    running: running.length,
    idle: devices.filter((x) => x.status === DEVICE_STATUS.IDLE).length,
    check: devices.filter((x) => x.status === DEVICE_STATUS.CHECK).length,
  };
};

// 전력 소비 트렌드 차트 API 파라미터 (시간별: 금일 시간대별, 일자별: 이번 달 1일~오늘, 월별: 올해 1월 1일~오늘)
export const getChartParam = (period, now = moment()) => {
  const today = now.format('YYYY-MM-DD');

  if (period === PERIOD_TYPE.HOUR) {
    return { isPeriod: false, dateType: 'ymd', date: today, groupCols: ['ymd', 'hh'] };
  }

  const fromDate = (period === PERIOD_TYPE.DAY ? now.clone().startOf('month') : now.clone().startOf('year')).format(
    'YYYY-MM-DD'
  );
  return { isPeriod: true, dateType: 'ymd', fromDate, toDate: today, groupCols: ['ymd'] };
};

// 차트 API 응답 → [values, labels] (데이터 없는 구간은 0)
// 월별: 일자별 응답을 월별로 합산한 값
export const getChartSeries = (period, data = [], now = moment()) => {
  const powerBy = (keyFn) =>
    data.reduce((acc, item) => {
      const key = keyFn(item._id);
      acc[key] = (acc[key] || 0) + (Number(item.power) || 0);
      return acc;
    }, {});

  if (period === PERIOD_TYPE.HOUR) {
    const map = powerBy((id) => Number(id && id.hh));
    // 진행 중인 현재 시간대는 제외 (00시대에는 빈 차트 방지를 위해 0시만 표시)
    const hours = Array.from({ length: Math.max(now.hour(), 1) }, (_, i) => i);
    return [hours.map((h) => map[h] || 0), hours.map(hourLabel)];
  }

  if (period === PERIOD_TYPE.DAY) {
    const map = powerBy((id) => Number(String(id).slice(8, 10)));
    const days = Array.from({ length: now.date() }, (_, i) => i + 1);
    return [days.map((d) => map[d] || 0), days.map((d) => `${d}일`)];
  }

  const map = powerBy((id) => Number(String(id).slice(5, 7)));
  const months = Array.from({ length: now.month() + 1 }, (_, i) => i + 1);
  return [months.map((m) => map[m] || 0), months.map((m) => `${m}월`)];
};

// 다음 24시간 전력수요 예측 및 피크
export const getForecast = (kw) => {
  const values = Array.from({ length: 24 }, (_, i) =>
    Math.max(0, kw * (0.72 + 0.18 * Math.sin(((i - 4) * Math.PI) / 12) + 0.09 * Math.cos(i * 1.3)))
  );
  const peak = Math.max(...values);
  const peakIdx = values.indexOf(peak);

  return {
    values,
    labels: Array.from({ length: 24 }, (_, i) => hourLabel(i)),
    peak,
    peakTime: `${String(peakIdx).padStart(2, '0')}:00`,
    energy: sum(values, (x) => x),
  };
};

export const validateHeatcast = (o) => {
  if (!o || typeof o !== 'object') throw Error('JSON 객체를 입력해 주세요.');
  HEATCAST_NUMBER_KEYS.forEach((k) => {
    if (o[k] !== undefined && o[k] !== null && (typeof o[k] !== 'number' || !Number.isFinite(o[k]))) {
      throw Error(k + ' 값은 숫자여야 합니다.');
    }
  });
  return o;
};

// CBL 평균: cbl_kw 우선, 없으면 dr.baseline 평균
export const getCblAverage = (o) => {
  if (typeof o.cbl_kw === 'number' && Number.isFinite(o.cbl_kw)) return o.cbl_kw;
  const list = o.dr && Array.isArray(o.dr.baseline)
    ? o.dr.baseline.filter((v) => typeof v === 'number' && Number.isFinite(v))
    : [];
  return list.length ? list.reduce((a, b) => a + b, 0) / list.length : null;
};
